'use client';

import clsx from 'clsx';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { useTranslations } from 'next-intl';
import type React from 'react';
import { type JSX, useRef, useState } from 'react';
import { Link } from '../Link';
import { TextButton } from '../TextButton';
import styles from './CategoryLinkCloud.module.scss';

interface CategoryLinkCloudListProps {
  links: CategoryLinkCloudItemProps[];
  categoryName: string;
  index: number;
  testId: string;
}

const CategoryLinkCloudList: React.FC<CategoryLinkCloudListProps> = ({
  links,
  categoryName,
  index,
  testId,
}) => {
  const t = useTranslations('other.common.actions');
  const [showMore, setShowMore] = useState(false);
  const listRef = useRef<HTMLUListElement>(null);

  const showMoreButtonTitle = showMore ? t('showLessLinks') : t('showMoreLinks');
  const showMoreButtonLabel = showMore ? t('showLess') : t('showMore');

  const handleShowMoreClick = () => {
    const newShowMoreState = !showMore;
    setShowMore(newShowMoreState);

    // When expanding, find the first link that becomes visible and move focus to it
    if (newShowMoreState && links.length > 5) {
      // Use setTimeout to ensure the DOM has updated before focusing
      setTimeout(() => {
        // Find the 5th link (index 5, the first one that was hidden before)
        const firstNewlyVisibleLink = listRef.current?.querySelector(
          `li:nth-child(6) a`,
        ) as HTMLAnchorElement | null;

        if (firstNewlyVisibleLink) {
          firstNewlyVisibleLink.focus();
        }
      }, 0);
    }
  };

  const showMoreButtonIcon = showMore ? <ChevronUp /> : <ChevronDown />;

  return (
    <>
      <ul className={styles.list} data-testid={testId} id={testId} ref={listRef}>
        {links.map((link, j) => (
          <li
            key={`category-link-cloud-${categoryName}-${j}`}
            className={clsx(styles.item, {
              [styles.itemHidden]: j > 4 && !showMore,
            })}
          >
            <Link
              href={link.href}
              title={link.title}
              ariaLabel={link.ariaLabel}
              target={link.target}
              rel={link.rel}
              className={styles.link}
              data-testid={`${testId}-category-${index + 1}-link-${j + 1}`}
            >
              {link.label}
            </Link>
          </li>
        ))}
      </ul>
      {links.length > 5 && (
        <div className={styles.showMore}>
          <TextButton
            onClick={handleShowMoreClick}
            title={showMoreButtonTitle}
            icon={showMoreButtonIcon}
            className={styles.showMoreButton}
            aria-expanded={showMore}
            aria-controls={testId}
            variant="primary"
            size="md"
          >
            {showMoreButtonLabel}
          </TextButton>
        </div>
      )}
    </>
  );
};

export interface CategoryLinkCloudItemProps {
  href: string;
  title: string;
  label: string;
  ariaLabel?: string;
  target?: string;
  rel?: string;
}

export interface CategoryLinkCloudCategoryProps {
  logo?: string;
  heading: string;
  description?: string;
  href: string;
  title: string;
  ariaLabel: string;
  links: CategoryLinkCloudItemProps[];
}

interface CategoryLinkCloudProps {
  categories: CategoryLinkCloudCategoryProps[];
  headingLevel?: 1 | 2 | 3 | 4 | 5 | 6;
  ariaLabel: string;
  testId: string;
}

export const CategoryLinkCloud: React.FC<CategoryLinkCloudProps> = ({
  categories,
  headingLevel = 2,
  ariaLabel,
  testId,
}) => {
  const t = useTranslations('components.categoryLinkCloud');
  const HeadingTag = `h${headingLevel}` as keyof JSX.IntrinsicElements;

  return (
    <nav className={styles.grid} aria-label={ariaLabel} data-testid={testId}>
      {categories.map((category, i) => (
        <article
          key={`category-link-cloud-${category.heading}`}
          className={styles.category}
          data-testid={`${testId}-category-${i + 1}`}
        >
          <a
            href={category.href}
            title={category.title}
            aria-label={category.ariaLabel}
            className={styles.categoryLink}
          >
            {category.logo && (
              <img
                src={category.logo}
                alt={t('logotypeAlt', { heading: category.heading })}
                className={styles.logo}
              />
            )}
            <HeadingTag className={styles.heading}>{category.heading}</HeadingTag>
          </a>
          {category.description && <p className={styles.description}>{category.description}</p>}
          <CategoryLinkCloudList
            links={category.links}
            categoryName={category.heading}
            index={i}
            testId={`${testId}-${category.heading.toLowerCase().replace(/\s+/g, '-')}`}
          />
        </article>
      ))}
    </nav>
  );
};
